import type { ColumnDef } from '@tanstack/react-table'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { bucketStarts, periodLabel, type RatingBucket, TrendPanel } from './RatingTrend'

import styles from './RatingTrend.module.scss'

import DataTable from '@/components/DataTable'
import { utcDay } from '@/utils/dates'

export type CountLine<T> = {
  key: string
  label: string
  color: string
  value: (bucket: T) => number
}
type Props<T> = {
  title: string
  description: string
  since: string
  until: string
  bucket: RatingBucket
  onBucket: (bucket: RatingBucket) => void
  buckets: T[]
  lines: CountLine<T>[]
  emptyMessage: string
}

const CountTrend = <T extends { start: string }>({
  title,
  description,
  since,
  until,
  bucket,
  onBucket,
  buckets,
  lines,
  emptyMessage,
}: Props<T>) => {
  const lookup = new Map(buckets.map((row) => [utcDay(row.start), row]))
  const points = bucketStarts(since, until, bucket).map((period) => {
    const row = lookup.get(period)
    return Object.fromEntries([['period', period], ...lines.map((line) => [line.key, row ? line.value(row) : 0])])
  }) as ({ period: string } & Record<string, number>)[]
  const columns: ColumnDef<(typeof points)[number]>[] = [
    {
      id: 'period',
      header: bucket === 'week' ? 'Week (UTC)' : 'Day (UTC)',
      accessorFn: (row) => periodLabel(row.period, bucket),
    },
    ...lines.map((line): ColumnDef<(typeof points)[number]> => ({
      id: line.key,
      header: line.label,
      accessorKey: line.key,
    })),
  ]
  const hasCounts = points.some((point) => lines.some((line) => point[line.key] > 0))

  return (
    <TrendPanel title={title} description={description} bucket={bucket} onBucket={onBucket}>
      {hasCounts ? (
        <>
          <div className={styles.chart}>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={points} margin={{ top: 12, right: 24, bottom: 0, left: 0 }} accessibilityLayer>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="period" tickFormatter={(value: string) => value.slice(5)} />
                <YAxis allowDecimals={false} width={56} />
                <Tooltip labelFormatter={(label) => periodLabel(String(label), bucket) + ' (UTC)'} />
                <Legend />
                {lines.map((line) => (
                  <Line
                    key={line.key}
                    dataKey={line.key}
                    name={line.label}
                    stroke={line.color}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <details className={styles.detail}>
            <summary>{bucket === 'week' ? 'View weekly counts' : 'View daily counts'}</summary>
            <DataTable data={[...points].reverse()} columns={columns} ariaLabel={title + ' by period'} />
          </details>
        </>
      ) : (
        <p className={styles.empty}>{emptyMessage}</p>
      )}
    </TrendPanel>
  )
}

export default CountTrend
